import { useEffect, useMemo } from 'react';
import { KeenIcon, DataGrid } from '@/components';
import { useGetDutyLiabilityByClubQuery } from '@/services/ClubResourceService';
import { DutyLiabilityMember } from '@/types/club-resource.types';
import usePagination from '@/hooks/usePagination';

interface MemberShiftsListingProps {
  clubId?: string;
}

export const MemberShiftsListing: React.FC<MemberShiftsListingProps> = ({
  clubId,
}) => {
  const { data, isLoading, isError, refetch } = useGetDutyLiabilityByClubQuery(
    clubId || '',
    { skip: !clubId, refetchOnMountOrArgChange: true }
  );

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const summary = data?.data;
  const allMembers: DutyLiabilityMember[] = useMemo(
    () => summary?.members || [],
    [summary?.members]
  );

  const getStatusMeta = (member: DutyLiabilityMember) => {
    switch (member.obligation_status) {
      case 'NOT_APPLICABLE':
        return { label: 'Not applicable', className: 'badge-light' };
      case 'FULFILLED_BY_COMPLETED_DUTY':
        return { label: 'Fulfilled by duty', className: 'badge-success' };
      case 'FULFILLED_BY_BUYOUT':
        return { label: 'Fulfilled by Buy-Out', className: 'badge-info' };
      case 'FULFILLED_BY_MIXED':
        return { label: 'Fulfilled mixed', className: 'badge-success' };
      default:
        return { label: 'Open', className: 'badge-warning' };
    }
  };

  // Filter by search on name or email
  const filteredMembers = useMemo(() => {
    const q = (debouncedValue || '').toString().trim().toLowerCase();
    if (!q) return allMembers;
    return allMembers.filter((m) => {
      const name = (m.name || '').toLowerCase();
      const email = (m.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [allMembers, debouncedValue]);

  // pagination bookkeeping for DataGrid UI consistency
  useEffect(() => {
    const count = filteredMembers.length;
    handleTotalItems(count);
    const pages = Math.max(1, Math.ceil(count / pagination.pageSize));
    handleTotalPages(pages);
    // if current page out of bounds, reset
    if (pagination.pageIndex > pages - 1) {
      setPagination({ ...pagination, pageIndex: 0 });
    }
  }, [
    filteredMembers.length,
    handleTotalItems,
    handleTotalPages,
    pagination,
    setPagination,
  ]);

  const pagedMembers = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredMembers.slice(start, end);
  }, [filteredMembers, pagination.pageIndex, pagination.pageSize]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'member',
        header: 'Member',
        enableSorting: false,
        cell: ({ row }: { row: { original: DutyLiabilityMember } }) => {
          const m = row.original;
          const displayName =
            m.name && m.name.trim().length > 0 ? m.name : m.email;
          return (
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-900">
                {displayName}
              </span>
              {m.name && m.email && m.name !== m.email && (
                <span className="text-xs text-gray-500">{m.email}</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'required_duties',
        header: 'Required Duties',
        enableSorting: false,
        cell: ({ row }: { row: { original: DutyLiabilityMember } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.required_duties}
          </span>
        ),
      },
      {
        accessorKey: 'completed_duties',
        header: 'Duty Done',
        enableSorting: false,
        cell: ({ row }: { row: { original: DutyLiabilityMember } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.completed_duties}
          </span>
        ),
      },
      {
        accessorKey: 'buyout_duties',
        header: 'Buy-Outs',
        enableSorting: false,
        cell: ({ row }: { row: { original: DutyLiabilityMember } }) => (
          <div className="flex flex-col">
            <span className="text-gray-800 font-normal">
              {row.original.buyout_duties || 0}
            </span>
            {(row.original.buyout_amount_total || 0) > 0 && (
              <span className="text-xs text-gray-500">
                €{Number(row.original.buyout_amount_total).toFixed(2)}
              </span>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'open_duties',
        header: 'Open Duties',
        enableSorting: false,
        cell: ({ row }: { row: { original: DutyLiabilityMember } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.open_duties ?? 0}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: DutyLiabilityMember } }) => {
          const meta = getStatusMeta(row.original);
          return (
            <span className={`badge ${meta.className}`}>{meta.label}</span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="w-full">
      <div className="card card-grid min-w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <label
                className="input input-sm h-8 sm:h-10 flex items-center"
                htmlFor="search-member-shifts"
                aria-label="Search members"
              >
                <KeenIcon
                  type="filled"
                  icon="magnifier"
                  className="text-gray-500"
                />
                <input
                  id="search-member-shifts"
                  placeholder="Search members"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search members by name or email"
                />
              </label>
            </div>
            {searchTerm && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={resetSearch}
                aria-label="Clear search filter"
                title="Clear search filter"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filter
              </button>
            )}
          </div>

          {/* Right side can be used for future actions; keeping layout consistent */}
          <div className="flex items-center gap-2"></div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="text-center py-4" role="status" aria-live="polite">
              Loading member shifts...
            </div>
          ) : isError ? (
            <div className="text-center py-4 text-red-500" role="alert">
              Failed to load member shifts. Please try again.
              <div className="mt-3">
                <button
                  onClick={() => refetch()}
                  className="btn btn-sm btn-light"
                >
                  <KeenIcon
                    type="filled"
                    icon="arrows-circle"
                    className="mr-2"
                  />
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <DataGrid
              columns={columns}
              data={pagedMembers}
              rowSelect={false}
              loading={isLoading}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              serverSide={false}
              onFilterReset={resetSearch}
              hasActiveFilters={!!searchTerm}
              aria-label="Member shifts duty table"
            />
          )}
        </div>
      </div>
    </div>
  );
};
